import { useContext, useEffect, useState } from 'react';
import Loading from '../../../components/Loading/Loading';
import Notification from '../../../components/UI/Notification/Notification';
import Title from '../../../components/UI/Title/Title';
import { useNavigate, Navigate } from 'react-router-dom';
import Button from '../../../components/UI/Button/Button';
import { AppContext } from '../../../context/App/AppContext';
import Filters from '../../../components/Filters/Filters';
import useFilters from '../../../components/Filters/useFilters';
import NoResultsFound from '../../../components/UI/NoResultsFound/NoResultsFound';
import { useMutation } from '@tanstack/react-query';
import { Switch } from 'antd';
import { editGym, refreshFetchFunctions } from '../../../apiFunctions/apiFunctions';
import { NotificationContext } from '../../../context/Notification/NotificationContext';
import { returnErrorFromResponse } from '../../../shared/utility';
import { isGymActive } from '../../../shared/gymFunctions';
import companyDetails from '../../../brandConfig/companyDetails';

export default () => {
  const navigate = useNavigate();
  const { userData, gymsList, gymsListIsError, gymsListIsLoading, onGymChange } = useContext(AppContext);
  const { openNotification } = useContext(NotificationContext);
  const { onFilters, filteredData, setFiltersInitialData } = useFilters();
  const [pendingGymStatuses, setPendingGymStatuses] = useState({});
  const { mutate: onUpdateGymStatus, isLoading: onUpdateGymStatusIsLoading } = useMutation({
    mutationFn: ({ gymId, active }) => editGym(gymId, { active }),
    onSuccess: (_, { gymId }) => {
      openNotification('save', 'success');
      refreshFetchFunctions([['gymsList'], ['selectedGym', gymId]]);
    },
    onError: (data, { gymId }) => {
      setPendingGymStatuses((state) => {
        let newState = { ...state };
        delete newState[gymId];
        return newState;
      });
      openNotification(null, 'error', returnErrorFromResponse(data));
    }
  });

  useEffect(
    () => {
      setFiltersInitialData(gymsList !== undefined ? gymsList : []);
    }, //eslint-disable-next-line
    [gymsList]
  );

  useEffect(() => {
    if (!gymsList) {
      return;
    }

    setPendingGymStatuses((state) => {
      let nextState = { ...state };

      Object.keys(state).forEach((gymId) => {
        const gym = gymsList.find((item) => item._id === gymId);
        if (gym && isGymActive(gym) === state[gymId]) {
          delete nextState[gymId];
        }
      });

      return nextState;
    });
  }, [gymsList]);

  if (!userData.isFullAccessAdminOrTrainer && userData.gymId.length === 1) {
    return <Navigate to={`/gym/${userData.gymId[0]}`} />;
  }

  if (gymsListIsLoading) {
    return <Loading />;
  }
  if (gymsListIsError) {
    return (
      <div className={'body-container'}>
        <Notification defaultMsg />
      </div>
    );
  }

  const handleGymChange = (gymId) => {
    onGymChange(gymId);
    navigate(`/gym/${gymId}`);
  };

  const returnGymActiveState = (gym) =>
    pendingGymStatuses[gym._id] !== undefined ? pendingGymStatuses[gym._id] : isGymActive(gym);

  const renderGymRow = (gym) => (
    <div
      key={gym._id}
      className={`listItem flex items-center justify-between gap-4 ${!returnGymActiveState(gym) ? 'listItemInactive' : ''}`}>
      <div onClick={() => handleGymChange(gym._id)} className="cursor-pointer">
        <div className="text-lg font-bold">{gym.name}</div>
      </div>
      {userData.isFullAccessAdmin && (
        <div className="flex items-center gap-3">
          <div className="text-xs font-bold uppercase tracking-wide min-w-[64px] text-right">
            {returnGymActiveState(gym) ? 'Active' : 'Inactive'}
          </div>
          <Switch
            className="gymStatusSwitch"
            size="small"
            checked={returnGymActiveState(gym)}
            style={returnGymActiveState(gym) ? { backgroundColor: companyDetails.styles.secondary } : undefined}
            disabled={onUpdateGymStatusIsLoading}
            onClick={(checked, e) => e.stopPropagation()}
            onChange={(checked) => {
              setPendingGymStatuses((state) => ({ ...state, [gym._id]: checked }));
              onUpdateGymStatus({ gymId: gym._id, active: checked });
            }}
          />
        </div>
      )}
    </div>
  );

  const activeGyms = filteredData.filter((gym) => returnGymActiveState(gym));
  const inactiveGyms = filteredData.filter((gym) => !returnGymActiveState(gym));

  return (
    <div className="body-container">
      <div className="titleAndBtnContainer">
        <Title title={'Manage gyms'} />
        <Button label="Add" secondary onClick={() => navigate('/add-gym')} show={userData.isFullAccessAdmin} />
      </div>
      <Filters open filtersTypes={['name']} onFilters={onFilters} />

      <h2 className="text-secondary">Active gyms:</h2>
      {filteredData.length === 0 ? (
        <NoResultsFound text={'No gyms'} />
      ) : (
        <>
          {activeGyms.length === 0 ? <NoResultsFound text={'No active gyms'} /> : activeGyms.map(renderGymRow)}
          {inactiveGyms.length > 0 && (
            <>
              <h2 className="text-secondary mt-8">Inactive gyms:</h2>
              {inactiveGyms.map(renderGymRow)}
            </>
          )}
        </>
      )}
    </div>
  );
};
